import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IDilutorBath, StateOfBath, CompliantOfBath } from "../models/dilutorBath";
import { IDilutorBathApiResponse } from "../models/api/dilutorBathApiResponse";
import { IDilutorBathFilter } from "../models/filters/dilutorBathFilter";
import DilutorBathService from "../services/DilutorBathService";
import { IDilutorBathForm } from "../models/forms/dilutorBathForm";
import { ICenter } from "../models/center";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IDilutorControl } from "../models/dilutorControl";
import { IPowderType } from "../models/powderType";
import { useCenterStore } from "./center.store";
import { usePowderTypeStore } from "./powderType.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

export const useDilutorBathStore = defineStore("dilutorBaths", () => {
    // State
    const datas = ref<IDilutorBath[]>([]);
    const current = ref<IDilutorBath>({} as IDilutorBath);
    const itemForm = ref<IDilutorBathForm>({} as IDilutorBathForm);
    const itemsArray = ref<IDilutorBath[]>([]);
    const isLoading = ref<boolean>(false);

    // Getters
    /**
     * Retourne si au moins un bain dilueur en attente n'a pas été contrôlé
     */
    const isAnyPendingBathChecked = computed(() => (): boolean => {
        return itemsArray.value.some((b: IDilutorBath) => b.controls.length == 0);
    });

    /**
     * Retourne les bains en attente ayant été contrôlés
     */
    const getCheckedPendingBaths = computed(() => (): IDilutorBath[] | undefined => {
        if (itemsArray.value.length > 0)
            return itemsArray.value.filter((b) => b.controls.length > 0);
        else
            return undefined;
    });

    /**
     * Retourne la liste des bains dilueurs actifs
     * @returns Liste des bains
     */
    const getActiveBaths = computed(() => (): IDilutorBath[] | undefined => {
        return datas.value;
    });

    /**
     * Ne retourne que les bains créés entre les deux dates données
     */
    const getDilutorBathsCreatedDuringTimespan = computed(() => (date1: Date, date2: Date): IDilutorBath[] | undefined => {
        if (datas.value.length > 0)
            return datas.value.filter((b: IDilutorBath) => {
                let bTimestamp = new Date(b.addingDate!).setHours(0, 0, 0, 0);
                return bTimestamp >= date1.setHours(0, 0, 0, 0) && bTimestamp <= date2.setHours(0, 0, 0, 0);
            });
        else
            return undefined;
    });

    /**
     * Retourne les bains dilueurs du plus récent au plus ancien créé
     */
    const getBathsSortedByCreatedDateDesc = computed(() => (): IDilutorBath[] => {
        var dilutorBathList = datas.value.filter((x) => x.active == true && x.state.toString() != StateOfBath[StateOfBath.Archived].toString());

        if (dilutorBathList.length > 0)
            return dilutorBathList.sort((a, b) => new Date(b.addingDate!).getTime() - new Date(a.addingDate!).getTime());
        else
            return [];
    });

    /**
     * Retourne si au moins un bain a été créé entre les deux dates
     */
    const wereBathsCreatedDuringTimespan = computed(() => (date1: Date, date2: Date): boolean => {
        return datas.value.some((b) =>
            new Date(b.addingDate!).setHours(0, 0, 0, 0) >= new Date(date1).setHours(0, 0, 0, 0) &&
            new Date(b.addingDate!).setHours(0, 0, 0, 0) <= new Date(date2).setHours(0, 0, 0, 0)
        );
    });

    const getTop3Active = computed(() => {
        var dilutorBathList = datas.value.filter((x) =>
            x.active == true &&
            x.state.toString() != StateOfBath[StateOfBath.Archived].toString() &&
            (x.compliant.toString() == CompliantOfBath[CompliantOfBath.Compliant].toString() ||
                x.compliant.toString() == CompliantOfBath[CompliantOfBath.NonCompliantValidated].toString())
        );
        return dilutorBathList.slice(dilutorBathList.length >= 3 ? -3 : -3);
    });

    const getTopNumberActive = computed(() => (number: number) => {
        var dilutorBathList = datas.value.filter((x) =>
            x.active == true &&
            x.bathRemainingVolume > 0 &&
            x.state.toString() != StateOfBath[StateOfBath.Archived].toString() &&
            (x.compliant.toString() == CompliantOfBath[CompliantOfBath.Created].toString() ||
                x.compliant.toString() == CompliantOfBath[CompliantOfBath.Compliant].toString() ||
                x.compliant.toString() == CompliantOfBath[CompliantOfBath.NonCompliantValidated].toString())
        );

        return dilutorBathList
            .sort((a, b) =>
                new Date(a.addingDate!).getTime() - new Date(b.addingDate!).getTime() ||
                (b.state.toString() == StateOfBath[StateOfBath.Used] ? 1 : 0) - (a.state.toString() == StateOfBath[StateOfBath.Used] ? 1 : 0)
            )
            .slice(0, dilutorBathList.length >= number ? number : number);
    });

    /**
     * Retourne les N premiers bains actifs ayant été contrôlés (Conforme ou NonConformeValidé)
     * Utilisé pour proposer un changement de cuve en cours de production : les bains "non contrôlés"
     * (CompliantOfBath.Created) ne doivent pas être proposés automatiquement.
     */
    const getTopNumberControlled = computed(() => (number: number) => {
        var dilutorBathList = datas.value.filter((x) =>
            x.active == true &&
            x.bathRemainingVolume > 0 &&
            x.state.toString() != StateOfBath[StateOfBath.Archived].toString() &&
            (x.compliant.toString() == CompliantOfBath[CompliantOfBath.Compliant].toString() ||
                x.compliant.toString() == CompliantOfBath[CompliantOfBath.NonCompliantValidated].toString())
        );

        return dilutorBathList
            .sort((a, b) =>
                new Date(a.addingDate!).getTime() - new Date(b.addingDate!).getTime() ||
                (b.state.toString() == StateOfBath[StateOfBath.Used] ? 1 : 0) - (a.state.toString() == StateOfBath[StateOfBath.Used] ? 1 : 0)
            )
            .slice(0, dilutorBathList.length >= number ? number : number);
    });

    // Actions
    /**
     * Hydrate DilutorBaths from API response
     */
    async function hydrateDilutorBaths(baths: IDilutorBathApiResponse[]): Promise<IDilutorBath[]> {
        if (baths.length === 0) return [];

        // 1. Get stores
        const centerStore = useCenterStore();
        const powderTypeStore = usePowderTypeStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (powderTypeStore.datas.length === 0) {
            loadPromises.push(powderTypeStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const powderTypeMap = new Map(powderTypeStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: IDilutorBath[] = [];
        for (const bath of baths) {
            const model = { ...bath } as unknown as IDilutorBath;

            // Normaliser les enums : SignalR envoie des entiers, l'API HTTP envoie des strings.
            // Convertir les entiers (SignalR) en clé string pour que les comparaisons
            // .toString() == StateOfBath[StateOfBath.XXX] dans les getters fonctionnent.
            if (typeof (model.state as unknown) === 'number') {
                model.state = StateOfBath[model.state] as unknown as StateOfBath;
            }
            if (typeof (model.compliant as unknown) === 'number') {
                model.compliant = CompliantOfBath[model.compliant] as unknown as CompliantOfBath;
            }

            // Link references (O(1) via Map)
            // Fallback to API object if not in store
            model.center = centerMap.get(bath.centerId) ?? model.center;
            model.powderType = powderTypeMap.get(bath.powderTypeId) as IPowderType ?? model.powderType;

            // Controls are child entities - already included in API response
            // No need to hydrate them separately as they don't have nested objects

            models.push(model);
        }

        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns
     */
    async function load(filter: IDilutorBathFilter) {
        isLoading.value = true;
        let result = await DilutorBathService.getAll<IDilutorBathFilter, IDilutorBathApiResponse>(filter);
        isLoading.value = false;
        if (!result.error) {
            // Remplacer directement sans passer par datas = [] pour éviter un flash
            // vide dans la tuile entre le clear et la réhydratation (race condition).
            datas.value = await hydrateDilutorBaths(result.data as IDilutorBathApiResponse[]);
        } else {
            datas.value = [];
        }
        return result;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns
     */
    async function unusedBath(filter: IDilutorBathFilter, idBath: number, isCheck: boolean) {
        isLoading.value = true;
        let result = await DilutorBathService.UnusedBaths(idBath, isCheck, filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.err) {
            datas.value = await hydrateDilutorBaths(result.data as IDilutorBathApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un bain par son identifiant
     * @param id
     * @returns
     */
    async function get(id: number) {
        let result = await DilutorBathService.getById<IDilutorBathApiResponse>(id);
        // mettre à jour dans le store ?
        if (!result.error) {
            const [modelData] = await hydrateDilutorBaths([result.data as IDilutorBathApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            // on le stock dans le current puis dans le form
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     * @returns
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IDilutorBath | IDataOperationResult<IDilutorBath>>;
        if (itemForm.value.id > 0) {
            result = await DilutorBathService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateDilutorBaths([result.data as IDilutorBathApiResponse]);
                current.value = modelData;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IDilutorBath>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await DilutorBathService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDilutorBaths([result.data as IDilutorBathApiResponse]);
                current.value = modelData;
                // Ne pas pousser dans datas ici : le handler SignalR MajDilutorBath (Create)
                // s'en charge. Ajouter localement ET via SignalR causerait un doublon
                // car SignalR arrive avant la réponse HTTP dans la plupart des cas.
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await DilutorBathService.delete<IDilutorBathApiResponse>(current.value.id);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                // On remove l'élément des données
                datas.value.splice(index, 1);
            }

            current.value = newItem(undefined);
            itemForm.value = newForm(0);
        }
        return result;
    }

    function toForm(item: IDilutorBath): IDilutorBathForm {
        const form = {} as IDilutorBathForm;
        Object.assign(form, item);
        form.powderTypeId = item.powderType.id;
        form.centerId = item.center.id;
        return form;
    }

    // permet d'initialiser un objet vide pour le formulaire
    function newForm(lienId: number): IDilutorBathForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            name: "",
            powderLotNumber: "",
            powderExpiryDate: new Date(),
            bathAvailableVolume: 0,
            bathRemainingVolume: 0,
            state: StateOfBath.Pending,
            compliant: CompliantOfBath.Created,
            comment: "",
            powderTypeId: 0,
            controls: [] as Array<IDilutorControl>
        } as IDilutorBathForm;
        return itemForm.value;
    }

    // permet d'initialiser un objet vide pour les referentiels
    function newItem(lien?: ICenter): IDilutorBath {
        current.value = {
            id: 0,
            center: lien,
            name: "",
            powderLotNumber: "",
            powderExpiryDate: new Date(),
            bathAvailableVolume: 0,
            bathRemainingVolume: 0,
            state: StateOfBath.Pending,
            compliant: CompliantOfBath.Created,
            comment: "",
            powderType: { id: 0 } as IPowderType,
            controls: [] as Array<IDilutorControl>
        } as IDilutorBath;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        itemsArray,
        isLoading,
        // Getters
        isAnyPendingBathChecked,
        getCheckedPendingBaths,
        getActiveBaths,
        getDilutorBathsCreatedDuringTimespan,
        getBathsSortedByCreatedDateDesc,
        wereBathsCreatedDuringTimespan,
        getTop3Active,
        getTopNumberActive,
        getTopNumberControlled,
        // Actions
        hydrateDilutorBaths,
        load,
        unusedBath,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajDilutorBath", async (payload: IPayloadStore) => {
    const store = useDilutorBathStore();

    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateDilutorBaths([payload.data as IDilutorBathApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateDilutorBaths([payload.data as IDilutorBathApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
